import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useTranslations } from 'next-intl';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { DateString } from '@/lib/types/common';
import { Calendar } from '@/lib/ui-kit/calendar';
import { TimePicker } from '@/lib/ui-kit/time-picker';
import { cn } from '@/lib/ui-kit/utils';
import { Text } from './text';

// Ensure UTC plugin is loaded
dayjs.extend(utc);

type DateValue = DateString | Date | Dayjs;

// Display UTC time to the user
const getTimeInputValue = (date: DateValue) =>
  dayjs.isDayjs(date) ? date.format('HH:mm') : dayjs(date).utc().format('HH:mm');

const getTimeObject = (timeStr: string) => {
  const [h, m] = timeStr.split(':');
  return {
    hour: Number(h),
    minute: Number(m),
  };
};

type DateTimePickerProps = {
  value?: DateValue;
  onChange?: (value: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  calendarProps?: Omit<ComponentProps<typeof Calendar>, 'selected' | 'onSelect' | 'mode'>;
  timePickerProps?: Omit<ComponentProps<typeof TimePicker>, 'value' | 'onChange'>;
  onValidityChange?: (isValid: boolean) => void;
} & Omit<ComponentProps<'div'>, 'children' | 'onChange' | 'onSelect'>;

export const DateTimePicker = ({
  value,
  onChange,
  minDate,
  maxDate,
  calendarProps = {},
  timePickerProps = {},
  className,
  onValidityChange,
  ...props
}: DateTimePickerProps) => {
  const t = useTranslations('expiration');
  const [timeError, setTimeError] = useState<string | null>(null);

  const [innerValue, setInnerValue] = useState<Dayjs>(() => {
    const normalize = (d: Dayjs) => d.second(0).millisecond(0);

    const initialValue = value ? normalize(dayjs(value).utc()) : normalize(dayjs().utc());

    const now = normalize(dayjs().utc());

    if (minDate && initialValue.isBefore(dayjs(minDate).utc())) {
      return normalize(dayjs(minDate).utc());
    }

    if (initialValue.isBefore(now)) {
      return now;
    }

    return initialValue;
  });

  const reportValidity = useCallback(
    (isValid: boolean) => {
      onValidityChange?.(isValid);
    },
    [onValidityChange],
  );

  const time = useMemo(() => {
    return getTimeInputValue(innerValue);
  }, [innerValue]);

  const innerValueUtc = innerValue.utc();
  const utcYear = innerValueUtc.year();
  const utcMonth = innerValueUtc.month();
  const utcDay = innerValueUtc.date();

  const selectedDate = useMemo(
    () => new Date(utcYear, utcMonth, utcDay),
    [utcYear, utcMonth, utcDay],
  );

  // Project UTC calendar day onto a local Date so react-day-picker (which compares
  // by local calendar day) lines up with the UTC dates we render.
  const minDateUtc = useMemo(() => {
    if (!minDate) return undefined;
    const d = dayjs(minDate).utc();
    return new Date(d.year(), d.month(), d.date());
  }, [minDate]);

  const maxDateUtc = useMemo(() => {
    if (!maxDate) return undefined;
    const d = dayjs(maxDate).utc();
    return new Date(d.year(), d.month(), d.date());
  }, [maxDate]);

  const todayUtc = useMemo(() => {
    const d = dayjs().utc();
    return new Date(d.year(), d.month(), d.date());
  }, []);

  const handleTimeChange = useCallback(
    (timeStr: string) => {
      if (!timeStr || !/^\d{2}:\d{2}$/.test(timeStr)) return;

      let newValue = dayjs(innerValue).utc();
      const { hour, minute } = getTimeObject(timeStr);

      newValue = newValue
        .set('hour', hour)
        .set('minute', minute)
        .set('second', 0)
        .set('millisecond', 0);

      setInnerValue(newValue);
      onChange?.(newValue.toDate());

      const now = dayjs().utc().second(0).millisecond(0);
      if (newValue.isBefore(now)) {
        setTimeError(t('expirationPast'));
        reportValidity(false);
        return;
      }

      setTimeError(null);
      reportValidity(true);
    },
    [innerValue, onChange, reportValidity, t],
  );

  const handleDateChange = useCallback(
    (newDate?: Date) => {
      if (!newDate) return;

      const d = dayjs(newDate);

      const newValue = dayjs(innerValue)
        .utc()
        .set('year', d.year())
        .set('month', d.month())
        .set('date', d.date())
        .set('second', 0)
        .set('millisecond', 0);

      setInnerValue(newValue);
      onChange?.(newValue.toDate());

      const now = dayjs().utc().second(0).millisecond(0);
      if (newValue.isBefore(now)) {
        setTimeError(t('expirationPast'));
        reportValidity(false);
        return;
      }

      setTimeError(null);
      reportValidity(true);
    },
    [innerValue, onChange, reportValidity, t],
  );

  const handleSetNow = useCallback(() => {
    let next = dayjs().utc().second(0).millisecond(0);
    if (minDate) {
      const min = dayjs(minDate).utc().second(0).millisecond(0);
      if (next.isBefore(min)) next = min;
    }

    setInnerValue(next);
    onChange?.(next.toDate());
    setTimeError(null);
    reportValidity(true);
  }, [minDate, onChange, reportValidity]);

  return (
    <div className={cn('flex flex-col gap-4', className)} {...props}>
      <Calendar
        minDate={minDateUtc}
        maxDate={maxDateUtc}
        today={todayUtc}
        selected={selectedDate}
        onSelect={handleDateChange}
        {...calendarProps}
      />
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-[13px] leading-4 tracking-[-0.13px] text-v2-text-primary">
            {t('timeInUtc')}
          </span>
          <button
            type="button"
            onClick={handleSetNow}
            className="cursor-pointer text-[13px] leading-4 tracking-[-0.13px] text-v2-text-primary transition-opacity hover:opacity-80"
          >
            {t('now')}
          </button>
        </div>
        <TimePicker
          value={time}
          aria-invalid={!!timeError}
          onChange={(e) => handleTimeChange(e.target.value)}
          {...timePickerProps}
        />
        {timeError && (
          <Text variant="body-small" className="text-v2-red-400">
            {timeError}
          </Text>
        )}
      </div>
    </div>
  );
};
